import React, { useState, useEffect,  useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import MixSearch from '../../../Booking/Components/searchOptionComponents/MixSearch';
import TreeMenu from '../../../Vehicles/Components/searchOptionComponents/TreeMenu';
import TransportCompanyMenu from '../../../Vehicles/Components/searchOptionComponents/TransportCompanyMenu';
import VehiclesTable from '../../../Vehicles/Components/Tables/VehiclesTable';
import { getFetchData } from '../../../../actions/fetchDataAction';
import { showErrorToast } from '../../../../slices/toastSlice';
import { initialForm } from '../../../../actions/formAction';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Progress from '../../../../components/Loader/Progress ';

function AdvancedSearchVehiclesModal({
  open,
  onClose,
  onSelect,
  vehicles = [],
  autoFetchOnOpen = true,
  clientId,
  destinationId,
  listVehiclesNotShowing= [],
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const formReducer = useSelector((state) => state.form);
  const fetchData = useSelector((state) => state.fetchData);

  const [selectedVehicles, setSelectedVehicles] = useState(vehicles);
  const shipmentId = searchParams.get("id") || null;




  // Keep local selection in sync when modal opens or vehicles prop changes
  useEffect(() => {
    if (open) setSelectedVehicles(vehicles || []);
  }, [open, vehicles]);

  // Build query ensuring core filters; include ShipmentId only when provided.
  const buildQuery = useCallback(() => {
    const sp = new URLSearchParams(searchParams.toString());
    // Always enforce Status=2 (available vehicles?)
    sp.set('Status', '2');
    if (Array.isArray(clientId) && clientId.length > 0) {
      sp.set('Clients', clientId.join(','));
    } else if (clientId != null && clientId !== '') {
      sp.set('Clients', clientId);
    } else {
      sp.delete('Clients');
    }
    if (destinationId != null && destinationId !== '') sp.set('Destination', destinationId); else sp.delete('Destination');
    if (shipmentId != null && shipmentId !== '') sp.set('ShipmentId', shipmentId); else sp.delete('ShipmentId');
    return sp.toString();
  }, [searchParams, clientId, destinationId, shipmentId]);

  // Initialize filters and fetch on open; auto-refresh when filters (URL) change
  useEffect(() => {
    if (!open) return;
    dispatch(initialForm());
    if (autoFetchOnOpen) {
      
      dispatch(getFetchData({ query: buildQuery() }));
    }
  }, [open, autoFetchOnOpen, dispatch, buildQuery]);

  const isTableLoading = fetchData.loading === 'pending';

  const resetFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
    [
      'ShippingLine',
      'PortLoading',
      'Destination',
      'SearchOption',
      'Status',
      'AdvancedSearch',
      'MixSearch',
      'PageSize',
      'PageNumber',
      'SortOrder',
      'SortBy'
    ].forEach((k) => params.delete(k));
    navigate({ search: params.toString() });
  }, [location.search, navigate]);

  const handleSearch = useCallback(() => {
    if (!open) return;
    dispatch(getFetchData({ query: buildQuery() }));
  }, [open, dispatch, buildQuery]);

  const refreshTable = useCallback(() => {
    dispatch(getFetchData({ query: buildQuery() }));
  }, [dispatch, buildQuery]);

  // Selection handler (max 10)
  const handleSelectVehicle = useCallback(
    (e, vehicle) => {
      const checked = e?.target?.checked;
      if (checked) {
        if (selectedVehicles.find((v) => v.id === vehicle.id)) return;
        if (selectedVehicles.length >= 10) {
          dispatch(showErrorToast('You can only select up to 10 vehicles.'));
          return;
        }
        setSelectedVehicles((prev) => [...prev, vehicle]);
      } else {
        setSelectedVehicles((prev) => prev.filter((v) => v.id !== vehicle.id));
      }
    },
    [dispatch, selectedVehicles]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 600 }}>Advanced Search</DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {/* Filters */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(auto-fill,minmax(140px,1fr))',
              sm: 'repeat(auto-fill,minmax(160px,1fr))',
              md: 'repeat(auto-fill,minmax(180px,1fr))'
            },
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 2.5 }
          }}
        >
          {formReducer.loading === 'pending' || formReducer.loading === 'idle' ? (
            <Progress />
          ) : (
            <TreeMenu labelOpenButton="Vehicles" labelSelectAll="Select All" />
          )}
          <TransportCompanyMenu />
        </Box>

        {/* Search bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: { xs: 2, md: 2.5 },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between'
          }}
        >
          <MixSearch sx={{ minWidth: { xs: '100%', sm: 220 } }} />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' }
            }}
          >
            <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={handleSearch} sx={{ minWidth: 90 }}>
              Search
            </Button>
            <Button variant="outlined" size="small" onClick={resetFilters} sx={{ minWidth: 80 }}>
              Reset
            </Button>
          </Box>
        </Box>

        {/* Results table */}
        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: 160, sm: 200 },
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {isTableLoading ? (
            <Progress Msg="Loading ..." />
          ) : (
            <VehiclesTable
              isClient={false}
              selectedVehicle={selectedVehicles}
              handleSelectVehicle={handleSelectVehicle}
              handlingFetchData={refreshTable}
              withCheckbox={true}
              listVehiclesNotShowing={listVehiclesNotShowing}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onSelect?.(selectedVehicles);
            onClose();
          }}
          disabled={selectedVehicles.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AdvancedSearchVehiclesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  vehicles: PropTypes.arrayOf(PropTypes.object),
  autoFetchOnOpen: PropTypes.bool,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]).isRequired,
  destinationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  listVehiclesNotShowing: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default AdvancedSearchVehiclesModal;